import {ref, remove, set} from "firebase/database";
import {useObjectVal} from "react-firebase-hooks/database";
import {useAuthState} from 'react-firebase-hooks/auth';
import {database} from "hooks/firebase";
import {
  SurveyDatabaseModel,
  SurveyDatabaseValue,
  SurveyDefinitionModel,
  SurveyQuestionPage_RenderableModel,
  UserSurveyRenderableModel
} from "data/models/survey";
import {getAuth} from "firebase/auth";
import {
  BUSINESS_INFO_SURVEY,
  VISUAL_RECOMMENDATIONS,
  WHAT_MAKES_YOU_SPECIAL,
  WORDS_AND_MESSAGES,
  YOUR_CUSTOMERS,
  PREVIEWS_SURVEYS

} from "data/definition/surveys";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {useEffect, useMemo, useState} from "react";
import {DeliverableGroupKey, FeatureKey, FeatureLaneDefinitionModel} from "data/models/feature-lane";
import {QuestionKey, SurveyKey} from "data/models/survey/keys";
import {FeatureKeyForNavigation, SurveyKeyForNavigation} from "hooks/page-navigation/survey";
import {getFeatureLaneDefinitions} from "./feature-lanes";


export type UserSurveyDatabaseSetterValue = SurveyDatabaseValue | null;


export const SURVEY_ID_TO_USER_SURVEY: { [key in SurveyKey]: SurveyDefinitionModel } = {
  "business-info": BUSINESS_INFO_SURVEY,
  "what-makes-you-special": WHAT_MAKES_YOU_SPECIAL,
  "visual-recommendations": VISUAL_RECOMMENDATIONS,
  "your-customers": YOUR_CUSTOMERS,
  "words-and-messages": WORDS_AND_MESSAGES,
  "previews": PREVIEWS_SURVEYS,
};

type StorageQueueItem = {
  featureId: FeatureKey;
  surveyId: SurveyKey;
  questionId: QuestionKey;
  value: UserSurveyDatabaseSetterValue;
}

function resolveSurveyDefinition(
    deliverableGroupId: DeliverableGroupKey,
    featureIdForNavigation: FeatureKeyForNavigation,
    surveyIdForNavigation: SurveyKeyForNavigation,
): [FeatureLaneDefinitionModel, SurveyDefinitionModel] {
  if (surveyIdForNavigation !== "first" && surveyIdForNavigation !== "last"
      && (featureIdForNavigation === "first" || featureIdForNavigation === "last")
  ) {
    throw new Error(`Invalid navigableFeatureId/navigableSurveyId combination ${featureIdForNavigation}/${surveyIdForNavigation}. When featureId is "first" or "last", surveyId has to be "first" or "last" too.`);
  }

  const featureLaneDefinitions = getFeatureLaneDefinitions(deliverableGroupId);
  const featureDefinition = (() => {
    switch (featureIdForNavigation) {
      case "first":
        return featureLaneDefinitions[0];
      case "last":
        return featureLaneDefinitions[featureLaneDefinitions.length - 1];
      default:
        return featureLaneDefinitions.find(f => f.id === featureIdForNavigation);
    }
  })();
  if (featureDefinition === undefined) {
    throw new Error(`Invalid featureId: ${featureIdForNavigation}`);
  }
  switch (surveyIdForNavigation) {
    case "first":
      return [featureDefinition, SURVEY_ID_TO_USER_SURVEY[featureDefinition.userSurveys[0].id]];
    case "last":
      return [featureDefinition, SURVEY_ID_TO_USER_SURVEY[featureDefinition.userSurveys[featureDefinition.userSurveys.length - 1].id]];
    default:
      if (featureDefinition.userSurveys.find(us => us.id === surveyIdForNavigation) === undefined) {
        throw new Error(`The survey ${surveyIdForNavigation} does not belong to feature ${featureIdForNavigation}`)
      }
      return [featureDefinition, SURVEY_ID_TO_USER_SURVEY[surveyIdForNavigation]];
  }
}

export type SaveSurveyQuestionValue =
    ((featureId: FeatureKey, surveyId: SurveyKey, questionId: QuestionKey, value: UserSurveyDatabaseSetterValue) => Promise<void>);

export function useSurvey(
    deliverableGroupId: DeliverableGroupKey | undefined,
    featureIdForNavigation: FeatureKeyForNavigation | undefined,
    surveyIdForNavigation: SurveyKeyForNavigation | undefined,
):
    [
          UserSurveyRenderableModel | null,
          FeatureLaneDefinitionModel | null,
      boolean,
          Error | undefined,
          SaveSurveyQuestionValue | undefined,
    ] {
  const auth = getAuth();
  const [user, userLoading, userError] = useAuthState(auth);
  const [featureDefinition, surveyDefinition] = useMemo((): [FeatureLaneDefinitionModel | undefined, SurveyDefinitionModel | undefined] => {
    if (surveyIdForNavigation === undefined || featureIdForNavigation === undefined || deliverableGroupId === undefined) return [undefined, undefined];
    return resolveSurveyDefinition(deliverableGroupId, featureIdForNavigation, surveyIdForNavigation)
  }, [surveyIdForNavigation, featureIdForNavigation, deliverableGroupId]);

  const [surveyValues, surveyValuesLoading, surveyValuesError] =
      useObjectVal<SurveyDatabaseModel>((user && ref(database, `user-data/${user.uid}/user-surveys/${deliverableGroupId}/${featureDefinition?.id}/${surveyDefinition?.id}`)) ?? null);
  const [storageQueue, setStorageQueue] = useState<StorageQueueItem[]>([]);

  useEffect(() => {
    if (isNullOrUndefined(user) || storageQueue.length === 0) {
      return;
    }
    void storeQueuedValues();
  }, [user, storageQueue]);

  if (surveyDefinition === undefined || featureDefinition === undefined) {
    return [null, null, userLoading || surveyValuesLoading, userError || surveyValuesError, undefined];
  }

  async function storeQueuedValues() {
    if (storageQueue.length === 0) {
      return;
    }
    const promises = storageQueue.map(storageQueueItem => storeSurveyValue(
        storageQueueItem.featureId, storageQueueItem.surveyId, storageQueueItem.questionId, storageQueueItem.value
    ));
    setStorageQueue([]);
    return await Promise.all(promises);
  }

  async function saveSurveyQuestionValue(featureId: FeatureKey, surveyId: SurveyKey, questionId: QuestionKey, value: UserSurveyDatabaseSetterValue) {
    if (isNullOrUndefined(user)) {
      setStorageQueue([...storageQueue, {
        featureId,
        surveyId,
        questionId: questionId,
        value,
      }]);
      return;
    }
    return await storeSurveyValue(featureId, surveyId, questionId, value);
  }

  async function storeSurveyValue(featureId: FeatureKey, surveyId: SurveyKey, questionId: string, value: UserSurveyDatabaseSetterValue) {
    if (isNullOrUndefined(user)) {
      throw new Error("User must be defined.")
    }
    const valuePath = `user-data/${user.uid}/user-surveys/${deliverableGroupId}/${featureId}/${surveyId}/${questionId}`;
    if (value === null) {
      return await remove(ref(database, valuePath));
    }
    return await set(ref(database, valuePath), value);
  }

  const userSurvey: UserSurveyRenderableModel = {
    ...surveyDefinition,
    surveyQuestionPages: surveyDefinition.surveyQuestions.map(surveyQuestion => ({
      ...surveyQuestion,
      value: surveyValues?.[surveyQuestion.id],
    } as SurveyQuestionPage_RenderableModel)),
  }

  return [userSurvey, featureDefinition, userLoading || surveyValuesLoading, userError || surveyValuesError, saveSurveyQuestionValue];
}
